// Cost ledger, DE subscriptions, time entries and import batches (13 §Cost and time, §Imports).
import { z } from "zod";
import {
  AggregateGrain,
  ApprovalStatus,
  CostEventKind,
  Currency,
  MeterSettlementResolution,
  TimeEntryKind,
  Visibility,
} from "../enums";
import { MeterIdentity } from "../ingest";
import { IsoDate, MonthKey, PeriodKey, Timestamp, Uuid } from "../primitives";
import { CostEvent, DeSubscription, MeterSettlement, TimeEntry } from "../rows";
import {
  CompanyItemParams,
  CompanyParams,
  ImportBatch,
  ImportResult,
  ImportUpload,
  PageQuery,
  page,
} from "./common";
import { CostRollup } from "./org";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

export const CostQuery = PageQuery.extend({
  seat: Uuid.optional(),
  period: MonthKey.optional(),
  visibility: Visibility.optional().describe("agency rows need company#agency_operator"),
});

export const CostLedger = z
  .object({
    ledger: page(CostEvent),
    rollup: CostRollup,
  })
  .meta({ id: "CostLedger" });

/**
 * Manual post (e.g. an adjustment). Reversals go through `/costs/{id}/reverse`. The M3-01 agent
 * kinds are posted only from their one charge basis (`run.step_recorded`, `sandbox.stopped`;
 * `estimated` from subscription runs, ADR-0020), never by hand.
 */
export const CostCreate = z
  .strictObject({
    kind: CostEventKind.exclude([
      "reversal",
      "human_time",
      "agent_sandbox",
      "agent_storage",
      "agent_media",
      "estimated",
    ]),
    amount_minor: z.int(),
    currency: Currency,
    seat_id: Uuid.nullable().optional(),
    holder_membership_id: Uuid.nullable().optional(),
    occurred_at: Timestamp,
    meter_identity: MeterIdentity,
    visibility: Visibility.optional(),
    note: z.string().max(2000).optional().describe("Stored in source_ref.note"),
  })
  .meta({ id: "CostCreate" });

export const CostReverse = z
  .strictObject({ reason: z.string().trim().min(1).max(2000) })
  .meta({ id: "CostReverse" });

export const DeSubscriptionCreate = z
  .strictObject({
    seat_id: Uuid,
    membership_id: Uuid,
    amount_minor: z.int().min(0),
    currency: Currency,
    start_month: MonthKey,
    end_month: MonthKey.nullable().optional(),
    scope: Visibility,
  })
  .meta({ id: "DeSubscriptionCreate" });

export const DeSubscriptionPatch = z
  .strictObject({
    amount_minor: z.int().min(0).optional(),
    end_month: MonthKey.nullable().optional(),
    scope: Visibility.optional(),
  })
  .meta({ id: "DeSubscriptionPatch" });

export const TimeEntryQuery = PageQuery.extend({
  seat: Uuid.optional(),
  membership: Uuid.optional(),
  period: PeriodKey.optional(),
  kind: TimeEntryKind.optional(),
});

/**
 * Manual time entry (day or week aggregate). `approval_status` is server-set to `approved` for
 * managers; `visibility` defaults by kind (`founder` → `agency`).
 */
export const TimeEntryCreate = z
  .strictObject({
    seat_id: Uuid,
    membership_id: Uuid,
    kind: TimeEntryKind,
    grain: AggregateGrain,
    work_date: IsoDate,
    duration_seconds: z.int().positive(),
    visibility: Visibility.optional(),
    note: z.string().max(2000).nullable().optional(),
  })
  .meta({ id: "TimeEntryCreate" });

/** A seconds change creates revision N+1; an approval change is a lifecycle update. */
export const TimeEntryPatch = z
  .strictObject({
    duration_seconds: z.int().positive().optional(),
    note: z.string().max(2000).nullable().optional(),
    approval_status: ApprovalStatus.optional(),
    rejection_reason: z.string().max(2000).nullable().optional(),
  })
  .meta({ id: "TimeEntryPatch" });

export const ImportReversal = z
  .object({
    batch_id: Uuid,
    voided_time_entries: z.int().min(0),
    tombstoned_observations: z.int().min(0),
    reversed_cost_events: z.int().min(0),
    closed_external_identities: z.int().min(0),
    deleted_tasks: z.int().min(0).optional().describe("M2-08: a `tasks` batch"),
  })
  .meta({ id: "ImportReversal" });

/**
 * D-15 (M3-04): an agency operator settles a meter identity a source keeps re-sending with other
 * content (409 `meter_conflict` for its whole batch). `repost`: reverse the posted row and post
 * the source's amount under `{identity}:r{n}`; `reconcile`: keep the ledger as it is. Either way a
 * re-send of exactly the settled content is then a `duplicate`.
 */
export const MeterSettle = z
  .strictObject({
    meter_identity: MeterIdentity,
    resolution: MeterSettlementResolution,
    amount_minor: z.int().min(0).describe("The amount the source reports"),
    amount_micros: z.int().min(0).nullable().optional(),
    reason: z.string().min(1).max(500),
  })
  .meta({ id: "MeterSettle" });

export const ledgerRoutes = {
  "costs.list": defineRoute({
    ...core,
    id: "costs.list",
    method: "get",
    path: `${base}/costs`,
    tag: "costs",
    summary: "Cost ledger and roll-up (rows filtered by visibility and role)",
    access: "reader",
    params: CompanyParams,
    query: CostQuery,
    response: { status: 200, schema: CostLedger },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "costs.create": defineRoute({
    ...core,
    id: "costs.create",
    method: "post",
    path: `${base}/costs`,
    tag: "costs",
    summary: "Post a manual cost row (manager)",
    access: "manager",
    params: CompanyParams,
    body: CostCreate,
    idempotency: "optional",
    response: { status: 201, schema: CostEvent },
    errors: ["meter_conflict"],
    emits: ["cost.posted"],
  }),
  "costs.reverse": defineRoute({
    ...core,
    id: "costs.reverse",
    method: "post",
    path: `${base}/costs/{id}/reverse`,
    tag: "costs",
    summary: "Post the reversal row (negative, reverses_cost_event_id)",
    access: "manager",
    params: CompanyItemParams,
    body: CostReverse,
    idempotency: "optional",
    response: { status: 201, schema: CostEvent },
    errors: ["conflict"],
    emits: ["cost.posted"],
  }),
  "costs.settleMeter": defineRoute({
    ...core,
    id: "costs.settleMeter",
    method: "post",
    path: `${base}/costs/meters/settle`,
    tag: "costs",
    summary: "Operator: settle a stuck meter identity (re-post the source's amount or reconcile)",
    access: "operator",
    params: CompanyParams,
    body: MeterSettle,
    idempotency: "required",
    response: { status: 201, schema: MeterSettlement },
    errors: ["conflict"],
    emits: ["cost.meter_settled", "cost.posted"],
  }),
  "costs.import": defineRoute({
    ...core,
    id: "costs.import",
    method: "post",
    path: `${base}/costs/import`,
    tag: "imports",
    summary: "CSV import of cost rows (dry_run supported; dedup via cost_meter)",
    access: "manager",
    params: CompanyParams,
    body: ImportUpload,
    bodyType: "multipart",
    idempotency: "optional",
    response: { status: 200, schema: ImportResult },
    errors: ["meter_conflict", "payload_too_large"],
    emits: ["cost.posted", "import.committed"],
  }),

  "deSubscriptions.list": defineRoute({
    ...core,
    id: "deSubscriptions.list",
    method: "get",
    path: `${base}/de-subscriptions`,
    tag: "costs",
    summary: "List DE subscriptions",
    access: "manager",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(DeSubscription) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "deSubscriptions.create": defineRoute({
    ...core,
    id: "deSubscriptions.create",
    method: "post",
    path: `${base}/de-subscriptions`,
    tag: "costs",
    summary: "Create a DE subscription (posted monthly by the scheduler)",
    access: "manager",
    params: CompanyParams,
    body: DeSubscriptionCreate,
    idempotency: "optional",
    response: { status: 201, schema: DeSubscription },
    errors: [],
    emits: ["de_subscription.created"],
  }),
  "deSubscriptions.update": defineRoute({
    ...core,
    id: "deSubscriptions.update",
    method: "patch",
    path: `${base}/de-subscriptions/{id}`,
    tag: "costs",
    summary: "Update a DE subscription",
    access: "manager",
    params: CompanyItemParams,
    body: DeSubscriptionPatch,
    response: { status: 200, schema: DeSubscription },
    // conflict: a DE's maintenance line (BILLING-PLANS) is changed on Operator › Billing
    errors: ["conflict"],
    emits: ["de_subscription.changed"],
  }),

  "timeEntries.list": defineRoute({
    ...core,
    id: "timeEntries.list",
    method: "get",
    path: `${base}/time-entries`,
    tag: "time",
    summary: "List live time entries (agency rows for operators only)",
    access: "reader",
    params: CompanyParams,
    query: TimeEntryQuery,
    response: { status: 200, schema: page(TimeEntry) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "timeEntries.create": defineRoute({
    ...core,
    id: "timeEntries.create",
    method: "post",
    path: `${base}/time-entries`,
    tag: "time",
    summary: "Record a time entry (recordTimeEntry)",
    access: "manager",
    params: CompanyParams,
    body: TimeEntryCreate,
    idempotency: "optional",
    response: { status: 201, schema: TimeEntry },
    errors: [],
    emits: ["time_entry.created", "cost.posted"],
  }),
  "timeEntries.update": defineRoute({
    ...core,
    id: "timeEntries.update",
    method: "patch",
    path: `${base}/time-entries/{id}`,
    tag: "time",
    summary: "Revise seconds (new revision) or decide approval",
    access: "manager",
    params: CompanyItemParams,
    body: TimeEntryPatch,
    response: { status: 200, schema: TimeEntry },
    errors: ["conflict"],
    emits: ["time_entry.updated", "time_entry.approval_changed", "cost.posted"],
  }),
  "timeEntries.delete": defineRoute({
    ...core,
    id: "timeEntries.delete",
    method: "delete",
    path: `${base}/time-entries/{id}`,
    tag: "time",
    summary: "Void the live row (never a DELETE)",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: TimeEntry },
    errors: [],
    emits: ["time_entry.deleted", "cost.posted"],
  }),
  "timeEntries.import": defineRoute({
    ...core,
    id: "timeEntries.import",
    method: "post",
    path: `${base}/time-entries/import`,
    tag: "imports",
    summary: "CSV import of day/week time aggregates (dry_run supported)",
    access: "manager",
    params: CompanyParams,
    body: ImportUpload,
    bodyType: "multipart",
    idempotency: "optional",
    response: { status: 200, schema: ImportResult },
    errors: ["tz_mismatch", "payload_too_large"],
    emits: [
      "time_entry.created",
      "time_entry.updated",
      "time_entry.approval_changed",
      "time_entry.deleted",
      "external_identity.created",
      "external_identity.closed",
      "cost.posted",
      "import.committed",
    ],
  }),

  "imports.list": defineRoute({
    ...core,
    id: "imports.list",
    method: "get",
    path: `${base}/imports`,
    tag: "imports",
    summary: "Committed import batches",
    access: "manager",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(ImportBatch) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "imports.reverse": defineRoute({
    ...core,
    id: "imports.reverse",
    method: "post",
    path: `${base}/imports/{batch}/reverse`,
    tag: "imports",
    summary: "Operator: reverse a batch (voids, tombstones, cost reversals)",
    access: "operator",
    params: z.object({ co: Uuid, batch: Uuid }),
    idempotency: "optional",
    response: { status: 200, schema: ImportReversal },
    errors: ["conflict"],
    emits: [
      "import.reversed",
      "time_entry.deleted",
      "observation.recorded",
      "cost.posted",
      "external_identity.closed",
      "task.deleted",
      "page.archived",
    ],
  }),
} as const;
